import React, { useEffect, useState } from "react";
import { User, Car, MapPin, ArrowRight } from "lucide-react";
import dayjs from "dayjs";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "../../../components/ui/avatar";

interface Props {
  booking: any;
  driver?: any;
  vehicle?: any;
}

export function BookingDetails({ booking, driver, vehicle }: Props) {
  const [passengersName, setPassengersName] = useState<string[]>([]);
  const [currencySymbol, setCurrencySymbol] = useState("R$");
  const [currencyMultiplier, setCurrencyMultiplier] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("Dinheiro");

  useEffect(() => {
    if (booking.passengersQty && booking.passengersQty >= 2) {
      const passengers = [booking.name];
      for (let i = 2; i <= booking.passengersQty; i++) {
        if (booking[`passenger${i}`]) {
          passengers.push(booking[`passenger${i}`]);
        }
      }
      setPassengersName(passengers);
    } else {
      setPassengersName([]);
    }
  }, [booking]);

  useEffect(() => {
    switch (booking.currency) {
      case "dollar":
        setCurrencySymbol("$");
        setCurrencyMultiplier(0.16);
        break;
      case "euro":
        setCurrencySymbol("€");
        setCurrencyMultiplier(0.16);
        break;
      case "pesos":
        setCurrencySymbol("₱");
        setCurrencyMultiplier(166.21);
        break;
      default:
        setCurrencySymbol("R$");
        setCurrencyMultiplier(1);
    }
  }, [booking.currency]);

  useEffect(() => {
    switch (booking.paymentMethod) {
      case "pix":
        setPaymentMethod("PIX");
        break;
      case "card":
        setPaymentMethod("Cartão");
        break;
      default:
        setPaymentMethod("Dinheiro");
    }
  }, [booking.paymentMethod]);

  const convertedValue = (booking.value / currencyMultiplier).toFixed(0);

  const detailRow = (label: string, value?: React.ReactNode) => (
    <div className="flex items-center justify-between text-white/70">
      <span className="text-white/60">{label}</span>
      <strong className="text-white/90">{value}</strong>
    </div>
  );

  return (
    <div className="mt-3 space-y-3 text-white">
      <div className="flex items-baseline justify-between">
        <div className="text-xl font-semibold text-primary">
          {currencySymbol} {Number(booking.value)}
          {booking.currency !== "real" && (
            <span className="ml-2 text-xs text-white/60">
              (R$ ~{convertedValue})
            </span>
          )}
        </div>
        <div className="text-xs text-white">
          {dayjs(booking.date).format("DD/MM/YYYY")} •{" "}
          {dayjs(booking.date).format("HH:mm")}
        </div>
      </div>

      {/* Origem e Destino lado a lado */}
      {(booking.origin || booking.destination) && (
        <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
          {booking.origin && (
            <>
              <MapPin className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate text-sm text-white/90">
                {booking.origin}
              </span>
            </>
          )}
          {booking.origin && booking.destination && (
            <ArrowRight className="h-4 w-4 shrink-0 text-white/40" />
          )}
          {booking.destination && (
            <span className="truncate text-sm text-white/90">
              {booking.destination}
            </span>
          )}
        </div>
      )}

      {/* Motorista e Veículo compactos */}
      <div className="flex flex-col gap-2">
        {driver && (
          <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-2.5 py-1.5">
            <Avatar className="h-6 w-6 shrink-0 border border-white/10">
              <AvatarImage src={driver.photo} alt={driver.name} />
              <AvatarFallback className="bg-white/10 text-white/60">
                <User className="h-3.5 w-3.5" />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-white/90">
                {driver.name}
              </p>
            </div>
          </div>
        )}
        {vehicle && (
          <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-2.5 py-1.5">
            <Avatar className="h-6 w-6 shrink-0 border border-white/10 rounded-lg">
              <AvatarImage
                src={vehicle.photo}
                alt={vehicle.name}
                className="object-cover"
              />
              <AvatarFallback className="bg-white/10 text-white/60 rounded-lg">
                <Car className="h-3.5 w-3.5" />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-white/90">
                {vehicle.name}
              </p>
              {vehicle.plate && (
                <p className="truncate text-[10px] text-white/50">
                  {vehicle.plate}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-1.5 text-xs">
        {detailRow("Pagamento", paymentMethod)}
        {booking.phone && detailRow("Telefone", booking.phone)}
        {detailRow("Passageiros", booking.passengersQty)}
        {passengersName.length > 0 && (
          <div className="space-y-1">
            <span className="text-white/60">Nomes</span>
            <div className="flex flex-wrap gap-1.5">
              {passengersName.map((name, index) => (
                <span
                  key={index}
                  className="inline-block rounded-md bg-white/10 px-2 py-0.5 text-[10px] text-white/90"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        )}
        {booking.flight && detailRow("Voo", booking.flight)}
        {booking.observation && (
          <div className="space-y-1">
            <span className="text-white/60">Obs</span>
            <div className="rounded-md bg-white/5 px-2.5 py-2 text-[11px] leading-relaxed text-white/90">
              {booking.observation}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
